/**
 * Project: spaces
 * File: Login
 * Created by Pennycodes on 2/4/2022.
 * Copyright spaces
 */
import React, { FormEvent, useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { toAbsoluteUrl } from "../../../layout/helpers";
import { PhoneInput, isPhoneValid } from "../../../components/ui/phone-input";
import * as localService from "../../../services/localService";
import apiService from "../../../services/apiService";
import { FaRegEye as FaEye, FaRegEyeSlash as FaEyeSlash } from "react-icons/fa";
import logger from "../../../utils/logger";
import { showApiMessage } from "../../../utils/messageHandler";

// Import Firebase
import { getAuth, signInWithPopup, GoogleAuthProvider } from "firebase/auth";
import { getMessaging, getToken } from "firebase/messaging";
import { getApp } from "firebase/app";
import { GoogleAuthResponse } from "../../../types";
import SEO from "../../../components/SEO/SEO";
import { SpaciaPasswordGuidelines } from "../../../components/SpaciaPasswordGuidelines";

// Types
interface ApiResponse {
  data: {
    data: any[];
    message?: string;
  };
  status: number;
}

const SignUp = () => {
  const history = useHistory();

  const [isLoading, setIsLoading] = useState(false);

  const [phoneValue, setPhoneValue] = useState("");
  // form
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("ROLE_HOST");
  const [confirmPasswordValidationError, setConfirmPasswordValidationError] = useState("");
  const [isEmptyFirstname, setIsEmptyFirstname] = useState(false);
  const [isEmptyLastname, setIsEmptyLastname] = useState(false);
  const [isEmptyEmail, setIsEmptyEmail] = useState(false);
  const [isEmptyPassword, setIsEmptyPassword] = useState(false);
  const [isInvalidConfirmPassword, setIsInvalidConfirmPassword] = useState(false);
  const [isRoleNotSelected, setIsRoleNotSelected] = useState(false);
  const [isInvalidPhoneNumber, setIsInvalidPhoneNumber] = useState(false);
  const [phoneNumberValidationError, setPhoneNumberValidationError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  function hasText(text: string) {
    return text != null && text !== "";
  }

  useEffect(() => {
    if (hasText(firstName)) {
      setIsEmptyFirstname(false);
    }
    if (hasText(lastName)) {
      setIsEmptyLastname(false);
    }
    if (hasText(email)) {
      setIsEmptyEmail(false);
    }
    if (hasText(password)) {
      setIsEmptyPassword(false);
    }
    if (hasText(confirmPassword)) {
      setIsInvalidConfirmPassword(false);
    }
    if (password && !confirmPassword) {
      setIsInvalidConfirmPassword(false);
    }
    if (role) {
      setIsRoleNotSelected(false);
    }
    if (phoneValue) {
      setIsInvalidPhoneNumber(false);
    }
  }, [firstName, lastName, email, password, confirmPassword, phoneValue, role]);

  const areFieldsOkay = () => {
    let ok = true;
    if (!hasText(firstName)) {
      setIsEmptyFirstname(true);
      ok = false;
    }
    if (!hasText(lastName)) {
      setIsEmptyLastname(true);
      ok = false;
    }
    if (!hasText(email)) {
      setIsEmptyEmail(true);
      ok = false;
    }
    if (!hasText(password)) {
      setIsEmptyPassword(true);
      ok = false;
    }
    if (!hasText(confirmPassword)) {
      setIsInvalidConfirmPassword(true);
      setConfirmPasswordValidationError("Confirm password is required");
      ok = false;
    } else if (password && confirmPassword && confirmPassword !== password) {
      setIsInvalidConfirmPassword(true);
      setConfirmPasswordValidationError("Passwords do not match");
      ok = false;
    }
    if (!role) {
      setIsRoleNotSelected(true);
      ok = false;
    }
    if (!phoneValue) {
      setIsInvalidPhoneNumber(true);
      setPhoneNumberValidationError("Please enter mobile number");
      ok = false;
    } else if (!isPhoneValid(phoneValue)) {
      setIsInvalidPhoneNumber(true);
      setPhoneNumberValidationError("Please enter a valid phone number for your selected country");
      ok = false;
    } else {
      setIsInvalidPhoneNumber(false);
    }

    return ok;
  };

  useEffect(() => {
    if (password && confirmPassword && confirmPassword !== password) {
      setIsInvalidConfirmPassword(true);
      setConfirmPasswordValidationError("Passwords do not match");
    }
  }, [confirmPassword]);

  const submitSignup = async (e: FormEvent) => {
    e.preventDefault();
    if (!areFieldsOkay()) return;

    setIsLoading(true);

    const userData = {
      avatar:
        "https://storage.googleapis.com/maestro-8beff.appspot.com/user-543-_1626834155761_69ec79f5_1b40_4716_9958_1a6f557f6581.jpeg",
      companyId: 1,
      confirmPassword,
      contacts: [
        {
          isContactable: true,
          isPrimary: true,
          type: "mobile",
          value: phoneValue,
        },
      ],
      firstName,
      lastName,
      password,
      username: email,
      role,
    };

    apiService
      .signup(userData)
      .then(response => {
        if (response.status === 201) {
          history.push("/confirm");
          localStorage.setItem("currentUser", response.data.data[0].username);
        }
      })
      .catch(err => {
        const errors = err.response?.data?.errors || [];
        logger.error(errors);
        showApiMessage(err, "sign up");
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const getFCMToken = async () => {
    try {
      const messaging = getMessaging(getApp());
      const token = await getToken(messaging);
      logger.log("FCM Token:", token);
      return token;
    } catch (error) {
      logger.error("Error getting FCM token:", error);
      return null;
    }
  };

  const onLoginSuccessful = (res: GoogleAuthResponse) => {
    logger.log(res.data);
    localService.saveUser(res.data.data[0]);
    const role = res.data.data[0].role;

    // Redirect based on role
    role === "ROLE_ADMINISTRATOR" ? history.push("/") : history.push("/startbooking");
  };

  const onLoginError = (error: any) => {
    logger.error("Login error:", error);
    showApiMessage(error, "google sign up");
  };

  const googleSignUp = async () => {
    try {
      const auth = getAuth();
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({
        prompt: "select_account",
      });

      const result = await signInWithPopup(auth, provider);
      const credential = GoogleAuthProvider.credentialFromResult(result);
      const accessToken = credential?.accessToken;

      if (!accessToken) {
        throw new Error("No access token received from Google");
      }

      const response = await apiService.googleAuth({
        accessToken,
        companyId: 1,
        provider: "GOOGLE",
      });

      const user = response.data.data[0];
      const fcmToken = await getFCMToken();

      if (fcmToken) {
        await apiService.updateFCMToken(user.id, fcmToken);
      }

      onLoginSuccessful(response);
    } catch (error) {
      logger.error("Google sign up error:", error);
      onLoginError(error);
    }
  };

  const togglePasswordVisibility = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <>
      <SEO
        title="Sign Up for Spacia | Create Your Account"
        description="Create your Spacia account to discover and book flexible workspaces, accommodations, and experiences across Africa. Join thousands of professionals today."
        keywords={["spacia sign up", "create spacia account", "register spacia", "spacia registration", "join spacia"]}
        url={typeof window !== "undefined" ? `${window.location.origin}/signup` : ""}
        noindex={true}
      />
      <div className="min-h-screen w-full bg-white">
        <div className="flex flex-col md:flex-row h-full min-h-screen">
          {/* Left Column - Signup Form */}
          <div className="w-full md:w-5/12 lg:w-5/12 xl:w-4/12 px-6 md:px-8 lg:px-12 flex flex-col justify-center">
            {/* Logo */}
            <div className="mb-6 mt-6 md:mt-0">
              <img
                alt="logo"
                src={toAbsoluteUrl("/assets/img/spacia/web/spacia_logo.png")}
                className="w-32 md:w-36 cursor-pointer"
                onClick={() => history.push("/home")}
              />
            </div>

            <form onSubmit={submitSignup} className="flex flex-col">
              <h1 className="text-2xl md:text-3xl font-semibold mt-4 mb-2">Welcome to SPACIA!</h1>
              {/* <h2 className="text-xl md:text-2xl font-medium mb-6">Sign Up</h2> */}

              {/* First Name Field */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="firstName">
                  First Name
                </label>
                <div className="relative">
                  <input
                    type="text"
                    id="firstName"
                    placeholder="John"
                    value={firstName}
                    onChange={e => setFirstName(e.target.value)}
                    className={`w-full px-4 py-3 bg-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 
                                        ${isEmptyFirstname ? "border border-red-500" : "border border-transparent"}`}
                  />
                  {isEmptyFirstname && <p className="mt-1 text-sm text-red-500">Please enter your first name</p>}
                </div>
              </div>

              {/* Last Name Field */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="lastName">
                  Last Name
                </label>
                <div className="relative">
                  <input
                    type="text"
                    id="lastName"
                    placeholder="Doe"
                    value={lastName}
                    onChange={e => setLastName(e.target.value)}
                    className={`w-full px-4 py-3 bg-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 
                                        ${isEmptyLastname ? "border border-red-500" : "border border-transparent"}`}
                  />
                  {isEmptyLastname && <p className="mt-1 text-sm text-red-500">Please enter your last name</p>}
                </div>
              </div>

              {/* Email Field */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="email">
                  Email
                </label>
                <div className="relative">
                  <input
                    type="email"
                    id="email"
                    placeholder="johndoe@gmail.com"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    className={`w-full px-4 py-3 bg-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 
                                        ${isEmptyEmail ? "border border-red-500" : "border border-transparent"}`}
                  />
                  {isEmptyEmail && <p className="mt-1 text-sm text-red-500">Please enter your email</p>}
                </div>
              </div>

              {/* Phone Number Field */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="phoneNumber">
                  Phone Number
                </label>
                <div>
                  <PhoneInput
                    defaultCountry="ke"
                    value={phoneValue}
                    onChange={val => setPhoneValue(val)}
                    error={isInvalidPhoneNumber}
                  />
                  {isInvalidPhoneNumber && <p className="mt-1 text-sm text-red-500">{phoneNumberValidationError}</p>}
                </div>
              </div>

              {/* Password Field */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="password">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    placeholder="••••••••••••"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    className={`w-full px-4 py-3 bg-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500
                                        ${isEmptyPassword ? "border border-red-500" : "border border-transparent"}`}
                  />
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    className="absolute top-1/2 right-3 transform -translate-y-1/2 text-gray-700"
                  >
                    {showPassword ? <FaEye /> : <FaEyeSlash />}
                  </button>
                  <SpaciaPasswordGuidelines />
                  {isEmptyPassword && <p className="mt-1 text-sm text-red-500">Please enter your password</p>}
                </div>
              </div>

              {/* Confirm Password Field */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="confirmPassword">
                  Confirm Password
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    id="confirmPassword"
                    placeholder="••••••••••••"
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    className={`w-full px-4 py-3 bg-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500
                                        ${isInvalidConfirmPassword ? "border border-red-500" : "border border-transparent"}`}
                  />
                  <button
                    type="button"
                    onClick={toggleConfirmPasswordVisibility}
                    className="absolute top-1/2 right-3 transform -translate-y-1/2 text-gray-700"
                  >
                    {showConfirmPassword ? <FaEye /> : <FaEyeSlash />}
                  </button>
                  {isInvalidConfirmPassword && (
                    <p className="mt-1 text-sm text-red-500">{confirmPasswordValidationError}</p>
                  )}
                </div>
              </div>

              {/* Role Field */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="role">
                  Role
                </label>
                <select
                  id="role"
                  value={role}
                  onChange={e => setRole(e.target.value)}
                  className={`w-full px-4 py-3 bg-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500
                                    ${isRoleNotSelected ? "border border-red-500" : "border border-transparent"}`}
                >
                  <option value="ROLE_HOST">Host</option>
                  <option value="ROLE_SUBSCRIBER_INDIVIDUAL">Subscriber Individual</option>
                </select>
                {isRoleNotSelected && <p className="mt-1 text-sm text-red-500">Please select a role</p>}
              </div>

              {/* Terms and Conditions */}
              <div className="flex items-center mb-6">
                <input
                  id="terms"
                  type="checkbox"
                  className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                />
                <label htmlFor="terms" className="ml-2 text-sm text-gray-700">
                  I agree to the Terms & Conditions and Privacy Policy
                </label>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-full py-3 px-6 mt-2 mb-4 flex items-center justify-center transition-colors duration-300"
                disabled={isLoading}
              >
                Create Account
                {isLoading && (
                  <svg
                    className="animate-spin ml-2 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                )}
              </button>

              {/* OAuth Buttons */}
              <div className="flex flex-col items-center">
                <button
                  type="button"
                  onClick={googleSignUp}
                  className="flex items-center justify-center w-full max-w-xs py-3 px-4 mb-3 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors duration-300"
                >
                  <img src={toAbsoluteUrl("/img/google.png")} alt="Google Sign In" className="w-5 h-5 mr-2" />
                  <span className="text-gray-700">Sign up with Google</span>
                </button>

                {/* Sign In Link */}
                <p className="mt-4 text-gray-700">
                  Already have an account?{" "}
                  <Link to="/signin" className="text-blue-600 hover:text-blue-800">
                    Sign In
                  </Link>
                </p>

                {/* Copyright */}
                <p className="mt-6 text-sm text-gray-500">&copy;2022 - 2024 SPACIA All rights reserved</p>
              </div>
            </form>
          </div>

          {/* Right Column - Background Image */}
          <div
            className="hidden md:block md:w-7/12 lg:w-7/12 xl:w-8/12 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: `url(${toAbsoluteUrl("/img/Slider.png")})`,
            }}
          ></div>
        </div>

        <style>
          {`
                .phone-input-wrapper .PhoneInput {
                    display: flex;
                    align-items: center;
                    background-color: rgb(243, 244, 246);
                    border-radius: 0.375rem;
                    padding: 0.5rem 1rem;
                }
                
                .phone-input-wrapper .PhoneInputInput {
                    flex: 1;
                    border: none;
                    outline: none;
                    background: transparent;
                    height: 2rem;
                }
                `}
        </style>
      </div>
    </>
  );
};

export default SignUp;
